import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, X, Spinner } from "@phosphor-icons/react";
import { assetUrl } from "@/lib/api";
import { uploadImage, IMAGE_ACCEPT } from "@/lib/uploadImage";
import { Field, ImageGuidelines } from "./Field";

const HERO_GUIDELINES = [
    "Aspect ratio: 16:9 (landscape). Recommended size: 1600 × 900 px (min width 1200 px).",
    "Format: JPG, PNG, WebP, GIF, or HEIC (iPhone). HEIC is auto-converted to JPG. Maximum file size: 5 MB.",
    "Keep the focal subject in the centre — the top + bottom may be cropped on small screens.",
];

export const CoverImageField = ({ value, onChange }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const onFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            onChange(url);
            toast.success("Hero image uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Field label="Hero Image">
            <input
                ref={inputRef}
                type="file"
                accept={IMAGE_ACCEPT}
                className="hidden"
                data-testid="editor-cover-input"
                onChange={(e) => onFile(e.target.files?.[0])}
            />
            {value ? (
                <div className="relative border border-zinc-800 bg-[#0A0A0A]" data-testid="editor-cover-preview">
                    <img
                        src={assetUrl(value) || undefined}
                        alt="hero preview"
                        className="w-full max-h-72 object-cover"
                    />
                    <div className="flex items-center gap-2 p-3 border-t border-zinc-800">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            data-testid="editor-cover-replace"
                            className="btn-secondary inline-flex items-center gap-2 text-xs disabled:opacity-60"
                        >
                            {uploading ? <Spinner size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
                            {uploading ? "Uploading…" : "Replace"}
                        </button>
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            data-testid="editor-cover-remove"
                            className="btn-secondary inline-flex items-center gap-2 text-xs"
                        >
                            <X size={14} weight="bold" /> Remove
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid="editor-cover-upload"
                    className="w-full border border-dashed border-zinc-700 hover:border-[#FFD000] hover:text-[#FFD000] text-zinc-400 py-10 text-center transition-colors disabled:opacity-60"
                >
                    <div className="flex flex-col items-center gap-2">
                        {uploading ? (
                            <Spinner size={28} className="animate-spin" />
                        ) : (
                            <UploadSimple size={28} weight="bold" />
                        )}
                        <span className="font-mono text-xs uppercase tracking-[0.2em]">
                            {uploading ? "Uploading…" : "Click to upload hero image"}
                        </span>
                    </div>
                </button>
            )}
            <ImageGuidelines title="Hero image guidelines" lines={HERO_GUIDELINES} />
        </Field>
    );
};
